import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import { createGame, step, WIDTH, HEIGHT } from '../../lib/arcade/snakeEngine';
import { render as renderFrame } from '../../lib/arcade/snakeRenderer';
import { createSnakeSound } from '../../lib/arcade/snakeSound';
import { createGameLoop } from '../../lib/arcade/gameLoop';
import ArcadeLeaderboard from './leaderboard';

/** Hidden-arcade Snake UI (C): controls, loop, HUD, sound and leaderboard. */
export const GAME_KEY = 'snake';

const HUD_RESERVED_PX = 170;
const TOUCH_CONTROLS_PX = 90;
const SWIPE_PX = 16;
const KEY_DIRECTIONS = {
    ArrowUp: 'up', KeyW: 'up', ArrowDown: 'down', KeyS: 'down',
    ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right',
};
const KEY_START = ['Space', 'Enter'];

function isTouchDevice() {
    if (typeof window === 'undefined') {
        return false;
    }
    return 'ontouchstart' in window || (window.innerWidth || 0) < 768;
}

@observer
class Snake extends Component {
    constructor(props) {
        super(props);
        const muted = !!util.get('arcade.muted');
        this.state = {
            screen: 'title',
            engineStatus: 'ready',
            score: 0,
            level: 1,
            length: 4,
            scale: 1,
            isTouch: isTouchDevice(),
            submitting: false,
            muted,
        };
        this.sound = createSnakeSound({ muted });
        this.loop = createGameLoop({ onTick: this.tick, onFrame: this.afterFrame });
        this.game = null;
        this.queuedDirection = null;
        this.touchStart = null;
        this.startedAt = 0;
        this.unmounted = false;
        this.canvasRef = null;
        this.wrapRef = null;
    }

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('resize', this.fitCanvas);
        window.addEventListener('blur', this.pause);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.fitCanvas();
        this.drawIdle();
        this.loadScores();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.scale !== this.state.scale) {
            const ctx = this.getCtx();
            if (ctx && this.game) {
                renderFrame(ctx, this.game);
            } else {
                this.drawIdle();
            }
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.loop.stop();
        this.sound.dispose();
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('resize', this.fitCanvas);
        window.removeEventListener('blur', this.pause);
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    loadScores = async () => {
        const { arcadeScoreStore } = this.props.stores;
        if (arcadeScoreStore?.loadLeaderboard) {
            await arcadeScoreStore.loadLeaderboard(GAME_KEY);
        }
    }

    getCtx() {
        return this.canvasRef ? this.canvasRef.getContext('2d') : null;
    }

    fitCanvas = () => {
        const wrapWidth = this.wrapRef ? this.wrapRef.clientWidth : WIDTH;
        const reserved = HUD_RESERVED_PX + (this.state.isTouch ? TOUCH_CONTROLS_PX : 0);
        const availableHeight = Math.max(HEIGHT, (window.innerHeight || HEIGHT) - reserved);
        const raw = Math.min(wrapWidth / WIDTH, availableHeight / HEIGHT);
        const scale = Math.max(1, Math.floor(raw * 2) / 2);
        if (scale !== this.state.scale) {
            this.setState({ scale });
        }
    }

    drawIdle() {
        const ctx = this.getCtx();
        if (ctx) {
            renderFrame(ctx, createGame({ rng: () => 0.55 }));
        }
    }

    startGame = () => {
        this.sound.unlock();
        this.game = createGame();
        this.queuedDirection = null;
        this.startedAt = Date.now();
        this.setState({
            screen: 'game',
            engineStatus: 'ready',
            score: 0,
            level: 1,
            length: 4,
            submitting: false,
        }, () => {
            if (!this.unmounted) {
                this.loop.start();
            }
        });
    }

    pause = () => {
        if (this.state.screen !== 'game') {
            return;
        }
        this.loop.stop();
        this.setState({ screen: 'paused' });
    }

    resume = () => {
        if (this.state.screen !== 'paused') {
            return;
        }
        this.sound.unlock();
        this.setState({ screen: 'game' }, () => {
            if (!this.unmounted) {
                this.loop.start();
            }
        });
    }

    togglePause = () => {
        if (this.state.screen === 'game') {
            this.pause();
        } else if (this.state.screen === 'paused') {
            this.resume();
        }
    }

    onVisibility = () => {
        if (document.hidden) {
            this.pause();
        }
    }

    close = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    toggleMute = (e) => {
        if (e?.currentTarget && typeof e.currentTarget.blur === 'function') {
            e.currentTarget.blur();
        }
        const muted = !this.state.muted;
        this.sound.setMuted(muted);
        util.set('arcade.muted', muted);
        this.setState({ muted });
    }

    tick = (dtMs) => {
        if (!this.game) {
            return;
        }
        step(this.game, { dir: this.queuedDirection }, dtMs);
        this.queuedDirection = null;
    }

    afterFrame = () => {
        if (!this.game) {
            return;
        }
        const events = this.game.events.splice(0);
        events.forEach(event => this.sound.play(event));
        const ctx = this.getCtx();
        if (ctx) {
            renderFrame(ctx, this.game);
        }
        this.syncHud();
        if (this.game.status === 'gameOver') {
            this.loop.stop();
            this.onGameOver();
        }
    }

    syncHud() {
        const score = this.game.score;
        const level = this.game.level;
        const length = this.game.snake.length;
        const engineStatus = this.game.status;
        if (score !== this.state.score || level !== this.state.level || length !== this.state.length
            || engineStatus !== this.state.engineStatus) {
            this.setState({
                score, level, length, engineStatus,
            });
        }
    }

    onGameOver = async () => {
        const { score, level } = this.game;
        const durationMs = Date.now() - this.startedAt;
        this.setState({ screen: 'gameOver', submitting: score > 0 });
        const { arcadeScoreStore } = this.props.stores;
        if (score > 0 && arcadeScoreStore?.submitScore) {
            await arcadeScoreStore.submitScore({
                game: GAME_KEY, score, wave: level, durationMs,
            });
            await arcadeScoreStore.loadLeaderboard(GAME_KEY);
        }
        if (!this.unmounted) {
            this.setState({ submitting: false });
        }
    }

    onKeyDown = (e) => {
        const { screen } = this.state;
        if (screen === 'game' && KEY_DIRECTIONS[e.code]) {
            this.queuedDirection = KEY_DIRECTIONS[e.code];
            e.preventDefault();
            return;
        }
        if ((screen === 'game' || screen === 'paused') && e.code === 'KeyP' && !e.repeat) {
            this.togglePause();
            e.preventDefault();
            return;
        }
        if ((screen === 'title' || screen === 'gameOver') && KEY_START.includes(e.code) && !e.repeat) {
            this.startGame();
            e.preventDefault();
        }
    }

    touchPoint(e) {
        const touch = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
        return touch ? { x: touch.clientX, y: touch.clientY } : null;
    }

    onCanvasTouchStart = (e) => {
        if (this.state.screen !== 'game') {
            return;
        }
        e.preventDefault();
        this.touchStart = this.touchPoint(e);
    }

    onCanvasTouchEnd = (e) => {
        if (this.state.screen !== 'game' || !this.touchStart) {
            return;
        }
        e.preventDefault();
        const end = this.touchPoint(e);
        if (!end) {
            return;
        }
        const dx = end.x - this.touchStart.x;
        const dy = end.y - this.touchStart.y;
        if (Math.max(Math.abs(dx), Math.abs(dy)) >= SWIPE_PX) {
            this.queuedDirection = Math.abs(dx) > Math.abs(dy)
                ? (dx > 0 ? 'right' : 'left')
                : (dy > 0 ? 'down' : 'up');
        }
        this.touchStart = null;
    }

    queueDirection = direction => (e) => {
        if (e?.cancelable) {
            e.preventDefault();
        }
        if (this.state.screen === 'game') {
            this.queuedDirection = direction;
        }
    }

    renderButtons() {
        const button = (direction, icon) => (
            <button
                type='button'
                class='btn btn-outline-light arcade-touch-btn'
                onTouchStart={this.queueDirection(direction)}
                onMouseDown={this.queueDirection(direction)}
            >
                <i class={`fa-solid ${icon}`} />
            </button>
        );
        return (
            <div
                class='d-flex justify-content-center align-items-center w-100 mt-3 gap-2'
                style={`max-width: ${WIDTH * this.state.scale}px;`}
            >
                {button('left', 'fa-caret-left')}
                {button('up', 'fa-caret-up')}
                {button('down', 'fa-caret-down')}
                {button('right', 'fa-caret-right')}
            </div>
        );
    }

    render() {
        const {
            screen, engineStatus, score, level, length, scale, isTouch, submitting, muted,
        } = this.state;
        const { arcadeScoreStore } = this.props.stores;
        const me = arcadeScoreStore?.leaderboardGame === GAME_KEY
            ? arcadeScoreStore.me : arcadeScoreStore?.bestByGame?.[GAME_KEY];
        const lastSubmit = arcadeScoreStore?.lastSubmit || null;
        const best = Math.max(me?.score || 0, lastSubmit?.best?.score || 0, score);
        const cssW = `${WIDTH * scale}px`;
        const cssH = `${HEIGHT * scale}px`;
        const inGame = screen === 'game' || screen === 'paused';

        return (
            <div
                class='d-flex flex-column align-items-center p-3 pb-5 bg-black text-white min-vh-100'
                ref={c => { this.wrapRef = c; }}
            >
                <div class='arcade-hud d-flex justify-content-between w-100 mb-2 small' style={`max-width: ${cssW};`}>
                    <span><Text id='arcade.score'>Score</Text> {score}</span>
                    <span><Text id='arcade.level'>Level</Text> {level}</span>
                    <span><Text id='arcade.length'>Length</Text> {length}</span>
                    <span><Text id='arcade.best'>Best</Text> {best}</span>
                    <button type='button' class='btn btn-sm btn-outline-light py-0 px-2 lh-1' onClick={this.toggleMute}>
                        <i class={`fa-solid ${muted ? 'fa-volume-xmark' : 'fa-volume-high'}`} />
                        <span class='visually-hidden'>
                            {muted ? <Text id='arcade.unmute'>Unmute</Text> : <Text id='arcade.mute'>Mute</Text>}
                        </span>
                    </button>
                </div>

                <div class='arcade-screen' style={`width: ${cssW}; height: ${cssH};`}>
                    <canvas
                        ref={c => { this.canvasRef = c; }}
                        width={WIDTH}
                        height={HEIGHT}
                        class='arcade-canvas'
                        style={`width: ${cssW}; height: ${cssH};`}
                        onTouchStart={this.onCanvasTouchStart}
                        onTouchEnd={this.onCanvasTouchEnd}
                    />

                    {screen === 'title' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.snake'>Snake</Text></h3>
                            <div class='small'>
                                {isTouch
                                    ? <Text id='arcade.controlsSnakeTouch'>Swipe or use the arrows to turn</Text>
                                    : <Text id='arcade.controlsSnakeKeyboard'>Arrows or WASD turn · P pauses</Text>}
                            </div>
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-success' onClick={this.startGame}>
                                    <Text id='arcade.start'>Start game</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                            </div>
                            <div class='small text-body-secondary'><Text id='arcade.insertCoin'>Press Space or tap Start</Text></div>
                        </div>
                    )}

                    {screen === 'game' && engineStatus === 'ready' && (
                        <div class='arcade-overlay'><Text id='arcade.ready'>Ready!</Text></div>
                    )}

                    {screen === 'paused' && (
                        <div class='arcade-overlay'>
                            <div><Text id='arcade.paused'>Paused — press P or Resume to continue</Text></div>
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-success btn-sm' onClick={this.resume}>
                                    <Text id='arcade.resume'>Resume</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light btn-sm' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {screen === 'gameOver' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.gameOver'>Game over</Text></h3>
                            <div><Text id='arcade.finalScore'>Final score</Text>: {score}</div>
                            {submitting && <div class='small'><Text id='arcade.savingScore'>Saving score…</Text></div>}
                            {!submitting && lastSubmit?.isNewBest && score > 0 && (
                                <div class='fw-bold'><Text id='arcade.newBest'>New personal best!</Text></div>
                            )}
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-success' onClick={this.startGame}>
                                    <Text id='arcade.playAgain'>Play again</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                                <button type='button' class='btn btn-outline-light' onClick={this.close}>
                                    <Text id='arcade.close'>Close</Text>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {isTouch && inGame && this.renderButtons()}

                {inGame && !isTouch && (
                    <div class='d-flex gap-2 mt-3'>
                        <button type='button' class='btn btn-outline-light btn-sm' onClick={this.togglePause}>
                            {screen === 'paused'
                                ? <Text id='arcade.resume'>Resume</Text>
                                : <Text id='arcade.pause'>Pause</Text>}
                        </button>
                        <button type='button' class='btn btn-outline-light btn-sm' onClick={this.close}>
                            <Text id='arcade.close'>Close</Text>
                        </button>
                    </div>
                )}

                {(screen === 'title' || screen === 'gameOver') && (
                    <div class='w-100' style={`max-width: ${cssW};`}>
                        <ArcadeLeaderboard stores={this.props.stores} game={GAME_KEY} />
                    </div>
                )}
            </div>
        );
    }
}

export default Snake;
